// src/context/AuthContext.js
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  clearApiSession,
  getApiErrorMessage,
  persistApiSession,
} from "../config/api";
import { authService } from "../services/authService";
import { planService } from "../services/planService";
import { profilerService } from "../services/profilerService";
import {
  hasActiveSuccessfulPlanPayment,
  resolveMarketplaceAccessState,
} from "../utils/marketplaceAccess";


const AuthContext = createContext(null);
const SESSION_KEY = "pms_session";

// ─────────────────────────────────────────────────────────────────────────────
// Role detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive role from account object.
 * The API does NOT return a role field — we detect via tenantId / sellerId.
 *   tenantId present + sellerId null → "tenant"
 *   sellerId present                 → "seller"
 */
function resolveAccess(account) {
  return {
    hasTenantAccess: Boolean(account?.tenantId || account?.tenant),
    hasSellerAccess: Boolean(account?.sellerId || account?.seller),
  };
}

function deriveRole(account) {
  const { hasTenantAccess, hasSellerAccess } = resolveAccess(account);

  if (hasSellerAccess) return "seller";
  if (hasTenantAccess) return "tenant";
  return "unknown";
}

function getDefaultPath(user) {
  return user?.hasTenantAccess ? "/tenant" : "/dashboard";
}

// ─────────────────────────────────────────────────────────────────────────────
// buildUser
// ─────────────────────────────────────────────────────────────────────────────

function buildUser(token, account) {
  const { hasTenantAccess, hasSellerAccess } = resolveAccess(account);
  const role = deriveRole(account);
  const isTenant = hasTenantAccess;

  const tenantInfo = account.tenant || {};
  const sellerInfo = account.seller || {};

  const firstName = tenantInfo.firstName || sellerInfo.firstName || "";
  const lastName = tenantInfo.lastName || sellerInfo.lastName || "";
  const email = tenantInfo.email || sellerInfo.email || "";

  const phone =
    account.phoneNumber ||
    tenantInfo.phoneNumber ||
    sellerInfo.phoneNumber ||
    "";
  const suffix = phone.replace(/\D/g, "").slice(-4) || "0000";

  const fullName =
    firstName || lastName
      ? `${firstName} ${lastName}`.trim()
      : `Profiler ${suffix}`;

  const initials =
    firstName && lastName
      ? (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
      : suffix.slice(-2).toUpperCase() || "PF";

  return {
    // ── Core identity ────────────────────────────────────────────────────
    id: account.id,
    phoneNumber: phone,
    sellerId: account.seller?.id || account.sellerId || null,
    sellerAccountId: account.seller?.id || account.sellerId || null,
    tenantId: account.tenantId || null,
    branchId: account.branchId || null,
    role, // "tenant" | "seller"
    token, // always access token as user.token across the app
    isArchived: account.isArchived ?? false,
    createdAt: account.createdAt,
    seller: account.seller || null,
    tenant: account.tenant || null,

    // ── Display ──────────────────────────────────────────────────────────
    firstName,
    lastName,
    email,
    name: fullName,
    initials,
    business: hasSellerAccess
      ? sellerInfo.shopName || "Seller Account"
      : "Tenant Account",
    shopName: sellerInfo.shopName || "",
    storeAddress: sellerInfo.storeAddress || "",
    phone,
    joinDate: account.createdAt
      ? new Date(account.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
      : "",
    bio: sellerInfo.note || "",
    location: hasSellerAccess ? sellerInfo.storeAddress || "" : "",

    // ── Plan (populated after plan selection or /me enrichment) ──────────
    plan: "",
    planId: null,
    planName: "",
    planPrice: null,
    planMaxProducts: null,
    planMaxCategories: null,
    planIsFree: null,
    marketName: "",
    marketCity: "",
    marketImage: "",
    marketDescription: "",

    // ── Seller enriched fields (from /me) ────────────────────────────────
    nicheCategories: [],
    products: [],
    productNames: [],
    incomingOrders: [],
    incomingOrderCount: 0,
    requestStatus: null,
    requestIsPaid: null,
    isBanned: false,
    rating: null,
    verified: false,

    // ── Flags ────────────────────────────────────────────────────────────
    isTenant,
    hasTenantAccess,
    hasSellerAccess,
    hasMarketplaceAccount: hasSellerAccess,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// enrichUserFromProfile — merges /profiler-account/me into the user object
// ─────────────────────────────────────────────────────────────────────────────

async function getActivePlanPayment(profilerId: string, token?: string): Promise<{
  id: string;
  transactionReference: string;
  planId: string;
  plan: Record<string, any>;
  isSuccessful: boolean;
  isSuccessfull?: boolean;
  isCanceled: boolean;
  isExpired?: boolean;
  expiresAt?: string;
  request: Record<string, any>;
} | null> {
  try {
    if (!profilerId || !token) {
      return null;
    }

    return await planService.getActivePlanPayment(profilerId);
  } catch {
    return null;
  }

}


async function enrichUserFromProfile(user, profile) {
  const sellerData = profile?.seller || null;
  const activePayment = await getActivePlanPayment(profile?.id, user?.token);

  const accessState = resolveMarketplaceAccessState({ user, profile, activePlanPayment: activePayment });
  let planData = sellerData?.plan || accessState.activePlan || {};
  const marketData = sellerData?.market || accessState.latestRequest?.market || {};
  let requestData = accessState.latestRequest || sellerData?.request || {};



  if (activePayment && hasActiveSuccessfulPlanPayment(activePayment)) {

    requestData = activePayment.request as any;
    planData = {
      id: activePayment.planId,
      ...activePayment.plan,
    }
  }

  const shopName = sellerData?.shopName || user.shopName || "";

  const planName = planData.name || "";


  const hasMarketplaceAccount = Boolean(sellerData?.id);
  const hasSellerAccess = Boolean(sellerData?.id);

  return {
    ...user,
    seller: sellerData,
    tenant: profile?.tenant || user.tenant || null,
    sellerId: sellerData?.id || null,
    sellerAccountId: sellerData?.id || null,
    name: user.firstName ? user.name : shopName || user.name,
    business:
      hasSellerAccess ? shopName || "Seller Account" : "Tenant Account",
    bio: sellerData?.note || user.bio || "",
    location: sellerData?.storeAddress || user.location || "",
    shopName,
    storeAddress: sellerData?.storeAddress || user.storeAddress || "",
    isBanned: sellerData?.isBanned ?? user.isBanned,
    plan: planData.name,
    planId: planData.id || null,
    planName,
    planPrice: planData.price ?? null,
    planMaxProducts: planData.maxProducts ?? null,
    planMaxCategories: planData.maxCategories ?? null,
    planIsFree: planData.isFree ?? null,

    marketName: marketData.name || "",
    marketCity: marketData.city || "",
    marketImage: marketData.image || "",
    marketDescription: marketData.description || "",

    requestStatus: requestData?.status || accessState.requestStatus || null,
    requestIsPaid: requestData?.isPaid ?? accessState.requestIsPaid ?? null,

    nicheCategories: sellerData?.nicheCategories || [],
    products: sellerData?.products || [],
    productNames: sellerData?.productNames || [],
    incomingOrders: profile.incomingOrders || [],
    incomingOrderCount: profile._count?.incomingOrders ?? 0,

    role: hasSellerAccess ? "seller" : user.hasTenantAccess ? "tenant" : user.role,
    isTenant: user.hasTenantAccess || profile.isTenant === true,
    hasTenantAccess:
      user.hasTenantAccess || Boolean(profile?.tenantId || profile?.tenant?.id),
    hasSellerAccess,
    hasMarketplaceAccount: hasMarketplaceAccount,
    verified: hasMarketplaceAccount,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// AuthProvider
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch /profiler-account/me and enrich the user.
   * Tenants skip this — their data comes from /tenants/profile/:phone.
   * Falls back to baseUser on any error so login is never blocked.
   */
  const fetchAndEnrich = useCallback(async (baseUser) => {
    if (!baseUser?.token) return baseUser;

    try {
      const profile = await profilerService.getMe(baseUser.token);

      if (!profile) {
        return baseUser;
      }

      return enrichUserFromProfile(baseUser, profile);
    } catch {
      return baseUser;
    }
  }, []);

  // Restore session from localStorage on mount
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem(SESSION_KEY);
        if (saved) {
          const { token, account } = JSON.parse(saved);
          if (token && account) {
            const base = buildUser(token, account);
            const enriched = await fetchAndEnrich(base);
            setUser(enriched);
          }
        }
      } catch {
        // Corrupt storage — silently ignore
      }
      setLoading(false);
    })();
  }, [fetchAndEnrich]);

  /**
   * Log in with phoneNumber + password.
   *
   * API returns: { token, account: { id, tenantId, sellerId, tenant: {…}, seller: {…}, … } }
   * Returns { success: true, role } or { success: false, error }.
   */
  const login = useCallback(
    async (phoneNumber, password) => {
      try {
        const data = await authService.login({ phoneNumber, password });

        const { token, account } = data;

        if (!token || !account) {
          return { success: false, error: "Unexpected response from server." };
        }

        if (account.isArchived) {
          return {
            success: false,
            error: "This account has been deactivated. Contact support.",
          };
        }

        if (!account.sellerId && !account.tenantId) {
          return {
            success: false,
            error: "Account type could not be determined. Contact support.",
          };
        }

        // Persist raw session payload for restore on refresh
        localStorage.setItem(SESSION_KEY, JSON.stringify({ token, account }));
        persistApiSession({ token, profilerId: account.id });

        const base = buildUser(token, account);
        const enriched = await fetchAndEnrich(base);

        setUser(enriched);
        return {
          success: true,
          role: enriched.role,
          hasTenantAccess: enriched.hasTenantAccess,
          hasSellerAccess: enriched.hasSellerAccess,
          defaultPath: getDefaultPath(enriched),
        };
      } catch (error) {
        return {
          success: false,
          error: getApiErrorMessage(
            error,
            "Network error. Check your connection and try again.",
          ),
        };
      }
    },
    [fetchAndEnrich],
  );

  /**
   * Called after a tenant successfully selects a marketplace plan.
   * Updates user state + persists the flag so it survives page refresh.
   */
  const setMarketplaceAccess = useCallback((planData) => {
    setUser((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        requestStatus: prev.requestStatus,
        requestIsPaid: prev.requestIsPaid,
        ...(planData && {
          planId: planData.id,
          planName: planData.name,
          planPrice: planData.price,
          planMaxProducts: planData.maxProducts,
          planMaxCategories: planData.maxCategories,
          planIsFree: planData.isFree ?? planData.price === 0,
          plan:
            planData.isFree || planData.price === 0
              ? "Free"
              : planData.name?.toLowerCase().includes("premium")
                ? "Premium"
                : "Advanced",
          marketName: planData.markets?.[0]?.name || "",
          marketCity: planData.markets?.[0]?.city || "",
          marketImage: planData.markets?.[0]?.image || "",
        }),
      };
    });
  }, []);

  /**
   * Log in with a raw bearer token (mobile → web bridge).
   */
  const loginWithToken = useCallback(async (token) => {
    try {
      const profile = await profilerService.getMe(token);

      if (!profile) return { success: false };

      const account = {
        id: profile.id,
        phoneNumber: profile.phoneNumber || "",
        sellerId: profile.sellerId || null,
        tenantId: profile.tenantId || null,
        branchId: profile.branchId || null,
        isArchived: profile.isArchived ?? false,
        createdAt: profile.createdAt,
        tenant: profile.tenant || {},
        seller: profile.seller || {},
      };

      if (account.isArchived) return { success: false };
      if (!account.sellerId && !account.tenantId) return { success: false };

      localStorage.setItem(SESSION_KEY, JSON.stringify({ token, account }));
      persistApiSession({ token, profilerId: account.id });

      const base = buildUser(token, account);
      const enriched = await enrichUserFromProfile(base, profile);
      setUser(enriched);

      return {
        success: true,
        role: enriched.role,
        hasTenantAccess: enriched.hasTenantAccess,
        hasSellerAccess: enriched.hasSellerAccess,
        defaultPath: getDefaultPath(enriched),
      };
    } catch {
      return { success: false };
    }
  }, []);

  /** Re-fetch /me and update stored user in place. */
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const enriched = await fetchAndEnrich(user);
    setUser(enriched);
  }, [user, fetchAndEnrich]);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(SESSION_KEY);
    clearApiSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithToken,
        logout,
        refreshProfile,
        setMarketplaceAccess,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
